/**
 * Coding sessions for the current app.
 *
 * Both coding pages render the same session rail, so the query lives here
 * rather than being written twice with two chances to diverge on its key. The
 * shared key also means creating a session on the index page invalidates the
 * list the detail page is about to show.
 *
 * @module hooks/use-code-sessions
 */

import { useQuery } from "@tanstack/react-query"

import { listCodeSessions, type CodeSessionSummary } from "@/services/code-service"

/** Query key for the app's coding session list. */
export function codeSessionsKey(appId: string | undefined) {
  return ["code-sessions", appId ?? null] as const
}

export interface UseCodeSessionsResult {
  sessions: CodeSessionSummary[]
  loading: boolean
}

/** States where something is happening, so the rail's status dot is moving. */
const TRANSIENT: ReadonlySet<string> = new Set([
  "created",
  "provisioning",
  "running",
  "stopping",
])

export function useCodeSessions(appId: string | undefined): UseCodeSessionsResult {
  const query = useQuery({
    queryKey: codeSessionsKey(appId),
    queryFn: () => listCodeSessions(appId),
    // Without an app the list would span the whole workspace, which is not what
    // an app-scoped rail should show — wait for the route param instead.
    enabled: appId !== undefined,
    /*
      Polled, because the rail now reports live state — green for a running
      sandbox, a spinner while the agent works — and a list fetched once at
      mount would show yesterday's answer forever. Fast while anything is in
      flight, slow otherwise: an idle workspace should not ask every three
      seconds for a list that has not changed since the reaper last ran.
    */
    refetchInterval: (query) => {
      const rows = query.state.data ?? []
      return rows.some((session) => TRANSIENT.has(session.state)) ? 3000 : 15000
    },
  })

  return {
    sessions: query.data ?? [],
    loading: query.isLoading,
  }
}
